import { QRCodeCanvas } from 'qrcode.react';
import { useEffect } from 'react';

import { useGetUrlWithAnonymousId } from '@flipster/features/src/shared/hooks/useGetUrlWithAnonymousId';
import { zendesk } from '@flipster/features/src/shared/zendesk/url';
import { useOverlayItem } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { css } from '@flipster/styled-system/css';
import { divider, hstack, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { appDownloadEvent } from '@flipster/utils/segment/events/app-download';
import { FlipsterApkUrl } from '@flipster/utils/url';

import AppStoreIcon from '@web/assets/svgs/app/appstore.svg';
import GooglePlayIcon from '@web/assets/svgs/app/googleplay.svg';

interface Props {
  isShowAPK?: boolean;
}

export const AppDownloadModal = ({ isShowAPK }: Props) => {
  const { onClose } = useOverlayItem();
  const getUrlWithAnonymousId = useGetUrlWithAnonymousId();

  useEffect(() => {
    segment.track(appDownloadEvent.open);
  }, []);

  return (
    <Modal size='small'>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label='Download app' />
      </Modal.Header>
      <Modal.Content css={{ display: 'flex', justifyContent: 'center' }}>
        <div className={vstack({ gap: 40, mb: 24 })}>
          <div className={vstack({ bgColor: 'background.accent.primary', rounded: 8, p: 6, gap: 4 })}>
            <p className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-4' })}>QR download</p>
            <QRCodeCanvas value={getUrlWithAnonymousId('Download')} size={178} includeMargin />
          </div>

          <div className={hstack({ gap: 8 })}>
            <IsomorphicLink
              href={getUrlWithAnonymousId('PlaystoreDownload')}
              onClick={() => segment.track(appDownloadEvent.linkClick, { app_link: 'google_play' })}
              newTab
              aria-label='Get it on google play store'
            >
              <GooglePlayIcon />
            </IsomorphicLink>
            <IsomorphicLink
              href={getUrlWithAnonymousId('Download')}
              onClick={() => segment.track(appDownloadEvent.linkClick, { app_link: 'app_store' })}
              newTab
              aria-label='Get it on apple store'
            >
              <AppStoreIcon />
            </IsomorphicLink>
          </div>
          {isShowAPK && (
            <>
              <hr className={divider({ orientation: 'horizontal', color: 'border.default.level-1' })} />

              <div className={vstack({ w: '100%' })}>
                <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3', mb: 20 })}>
                  If the Play Store is currently unavailable, please install the APK file.
                  <br />
                  {'For more information, refer to our '}
                  <a
                    className={css({ textStyle: 'Text/M/Underline', color: 'content.accent.info' })}
                    href={zendesk.getUrl('InstallationFlipsterApk')}
                  >
                    installation guide.
                  </a>
                </p>
                <Button
                  size='medium'
                  hierarchy='secondary'
                  color='neutral'
                  as='a'
                  href={FlipsterApkUrl}
                  label='Download via APK'
                />
              </div>
            </>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};
